import type { ScheduleEntry } from "./sentiment-check-slice";

/**
 * Find the next sentiment check date for the student.
 *
 * Consecutive schedule dates form windows: s[0]→s[1], s[1]→s[2], …
 * Returns the most recent unchecked window's date (triggers immediately),
 * or the next future schedule if the current window is done.
 *
 * Schedules [Feb 23, Mar 5, Mar 15, Mar 25]:
 *  - now=Mar 14, lastCheck=null  → "Mar 5"  (missed window, prompt now)
 *  - now=Mar 14, lastCheck=Mar 5 → "Mar 15" (current done, next window)
 *  - now=Feb 20, lastCheck=null  → "Feb 23" (wait for first schedule)
 *
 * @returns ISO date string or `null` if all checks are complete.
 */
export function findNextSchedule(
  schedules: ScheduleEntry[],
  lastCheckDate: string | null
): string | null {
  const now = new Date();
  const lastCheck = lastCheckDate ? new Date(lastCheckDate) : null;

  if (schedules.length === 0) return null;

  // Find the most recent schedule that has opened (scheduledAt <= now).
  // This is the start of the current check window.
  let currentIdx = -1;
  for (let i = schedules.length - 1; i >= 0; i--) {
    const entry = schedules[i];
    if (entry && new Date(entry.scheduledAt) <= now) {
      currentIdx = i;
      break;
    }
  }

  // No schedule has opened yet — return the first future schedule
  if (currentIdx === -1) {
    return schedules[0]?.scheduledAt ?? null;
  }

  // Check if the student has already completed a check in this window
  const current = schedules[currentIdx];
  if (!current) return null;

  const windowStart = new Date(current.scheduledAt);

  if (!lastCheck || lastCheck < windowStart) {
    // Student hasn't checked in this window — return the window start
    // (which is <= now, so the check triggers immediately)
    return current.scheduledAt;
  }

  // Student already checked in this window — return the next schedule
  const next = schedules[currentIdx + 1];
  if (next) {
    return next.scheduledAt;
  }

  // All windows are complete
  return null;
}
